import React, {useEffect, useState} from "react";
import {
    useToast,
    FormControl,
    FormLabel,
    Flex,
    useDisclosure,
    IconButton,
    Text,
    Box, Checkbox,
} from "@chakra-ui/react";
import {parse, parseISO} from 'date-fns';
import {useDispatch} from "react-redux";
import {MainButton} from "../../components/Buttons/MainButton";
import {useAppSelector} from "../../redux/Store";
import {hasPermission, isRoleAdmin} from "../../features/Permission/AllowedAccess";
import {Product, ProductClass} from "../../shared/entities/Product/Product";
import {DealershipMinInfo} from "../../shared/entities/Users/Dealership";
import QuantityInput from "../../components/Inputs/QuantityInput";
import {
    getByProductAndDealership,
    getNextStartDate,
    getNextStartDates, getNextStartWeeklyDates,
    optionReorderValues, optionReorderWeeklyValues,
    ScheduledShipment,
    ScheduledShipmentSimple,
} from "../../shared/entities/Order/ScheduledShipment";
import {ScheduledShipmentController} from "../../controllers/ScheduledShipmentController";
import {Form, Formik} from "formik";
import {SelectControlStyled} from "../../components/Inputs/SelectControlStyled";
import {ScheduledValidator} from "../../controllers/Validation/ScheduledValidator";
import {format} from "date-fns";
import SchedulerMonths from "./SchedulerMonths";
import {ModalService} from "../../service/Modal/ModalService";
import {FiEdit} from "react-icons/fi";
import IconWithTooltip from "../../components/Icon/IconWithTooltip";
import {ActionTypes} from "../../redux/CurrentState/DragAndDropReducer";
import ResponsivePopoverDrawer from "./ResponsivePopoverDrawer";

interface ScheduledShipmentPopoverProps {
    onSuccess?: () => void;
    item?: Product;
    dealer?: DealershipMinInfo;
    editItem?: ScheduledShipment | ScheduledShipmentSimple;
    isDisabledSelDealer?: boolean;
    isSSTable?: boolean;
}

const AddScheduledShipmentPopover: React.FC<ScheduledShipmentPopoverProps> = ({
                                                                                  onSuccess,
                                                                                  item,
                                                                                  dealer,
                                                                                  editItem,
                                                                                  isDisabledSelDealer = false,
                                                                                  isSSTable = false,
                                                                              }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const scheduledShipmentController = new ScheduledShipmentController(dispatch);
    const user = useAppSelector((store) => store.AuthReducer.user);
    const modalService = new ModalService(dispatch);
    const adminDealerships = useAppSelector(
        (state) => state.UserReducer.dealershipsMinInfo
    );
    const dealerships = isRoleAdmin()
        ? adminDealerships
        : user?.dealerships?.map((v) => ({
        id: v.id,
        name: v.name,
    })) || [];
    const {isOpen, onOpen, onClose} = useDisclosure();
    const [localEditItem, setLocalEditItem] = useState<
        ScheduledShipment | ScheduledShipmentSimple | undefined
    >(editItem);


    useEffect(() => {
        setLocalEditItem(editItem);
    }, [editItem]);

    const [initialValues, setInitialValues] = useState<any>({
        id: -1,
        reorderPeriod: 0,
        dealershipId: dealer ? Number(dealer.id) : 0,
        isAdmin: false,
        userId: user ? Number(user.id) : 0,
        productId: item ? item.id : 0,
        nextStartDate: format(new Date(getNextStartDate()), "yyyy-MM-dd"),
        quantity: 0,
    });

    useEffect(() => {
        if (initialValues.dealershipId && item && item.shipments) {
            let shipmentsDealer = getByProductAndDealership(
                item,
                Number(initialValues.dealershipId)
            );
            if (shipmentsDealer) {
                setLocalEditItem(shipmentsDealer);
            }
        }
    }, [initialValues.dealershipId]);

    useEffect(() => {
        let nextStartDateValue = format(localEditItem ? parse(localEditItem.nextStartDate, 'MM.dd.yyyy', new Date()) : new Date(getNextStartDate()), "yyyy-MM-dd")
        setInitialValues((prev: any) => ({
            ...prev,
            id: localEditItem ? localEditItem.id : -1,
            isAdmin: localEditItem ? localEditItem.isAdmin : false,
            reorderPeriod: localEditItem ? localEditItem.reorderPeriod : 0,
            dealershipId: localEditItem
                ? localEditItem.dealershipId
                : dealer
                    ? Number(dealer.id)
                    : 0,
            productId: item ? item.id : 0,
            nextStartDate: nextStartDateValue,
            quantity: localEditItem ? localEditItem.quantity : 0,
        }));
        if (item && editItem) {
            item.quantity = localEditItem ? localEditItem.quantity : 0;
        }
    }, [dealer, localEditItem, item, getNextStartDate]);

    const confirmRemove = (id: number) => {
        modalService.setModalData({
            onSubmit: () => {
                modalService.deleteModalData();
                scheduledShipmentController.remove({id: id}).then((res) => {
                    toast({
                        position: "top",
                        title: res.None
                            ? `Failed to remove ID: ${id}`
                            : `Success removed. ID: ${id}`,
                        status: res.None ? "error" : "success",
                        duration: 3000,
                        isClosable: true,
                    });
                    if (onSuccess) {
                        dispatch({type: ActionTypes.CHANGE_PRODUCT});
                        scheduledShipmentController.all({data: {dealershipId: initialValues.dealershipId}});
                        onSuccess();
                    }
                    if (onClose) {
                        onClose();
                    }
                });
            },
            isOpen: true,
            onOpen: () => {
            },
            onClose: () => {
                modalService.deleteModalData();
            },
            modalTextData: "Are you sure you want to remove item?",
            context: "action",
        });
    };

    const renderFormContent = () => {
        return (
            <Box>
                <Formik
                    initialValues={initialValues}
                    validate={(values) => {
                        console.log(values);
                        setInitialValues(values);
                    }}
                    enableReinitialize={true}
                    validationSchema={ScheduledValidator.GetSchema()}
                    onSubmit={(values, actions) => {
                        scheduledShipmentController.create(values).then((res) => {
                            actions.setSubmitting(false);
                            const isError = !!res.None;
                            toast({
                                position: "top",
                                title: isError ? "Failed to add item." : "Added successfully.",
                                status: isError ? "error" : "success",
                                duration: 5000,
                                isClosable: true,
                            });
                            dispatch({type: ActionTypes.CHANGE_PRODUCT});
                            if (onSuccess) {
                                scheduledShipmentController.all({
                                    data: {dealershipId: values.dealershipId},
                                });
                                dispatch({type: ActionTypes.CHANGE_PRODUCT});
                                onSuccess();
                            }
                            if (onClose) {
                                onClose();
                            }
                        });
                        actions.setSubmitting(false);
                    }}
                >
                    {({isSubmitting, errors, values, setFieldValue, touched}) => (
                        <FormControl>
                            <Form>
                                <Flex py={1}>
                                    <Box width={"40%"}>
                                        <FormLabel fontSize={14} fontWeight={"md"}>
                                            Quantity:
                                        </FormLabel>
                                        <QuantityInput
                                            item={item ? item : new ProductClass()}
                                            error={!!(errors.quantity && touched.quantity)}
                                            width={"100%"}
                                            height={"40px"}
                                            onQuantityChange={(updatedItem) => {
                                                setFieldValue("quantity", updatedItem.quantity);
                                            }}
                                        />
                                        {errors.quantity && touched.quantity && (
                                            <Text fontSize={14} color={"red.500"}>
                                                {errors.quantity}
                                            </Text>
                                        )}
                                    </Box>
                                    <Box width={"60%"} pl={1}>
                                        <FormLabel fontSize={14} fontWeight={"md"}>
                                            Scheduled every:{" "}
                                        </FormLabel>
                                        <SelectControlStyled selectProps={{fontSize: 14}} name="reorderPeriod">
                                            {(item?.subscriptionWeekly ? optionReorderWeeklyValues : optionReorderValues).map(({
                                                                                                                                   label,
                                                                                                                                   value
                                                                                                                               }) => (
                                                <option
                                                    style={{fontWeight: "400", fontSize: "14px"}}
                                                    key={Number(value)}
                                                    value={String(value)}
                                                >
                                                    {label}
                                                </option>
                                            ))}
                                        </SelectControlStyled>
                                    </Box>
                                </Flex>
                                <FormLabel fontSize={14} fontWeight={"md"}>
                                    Next Start Date:
                                </FormLabel>
                                <SelectControlStyled selectProps={{fontSize: 14}} name="nextStartDate">
                                    {(item?.subscriptionWeekly ? getNextStartWeeklyDates():getNextStartDates()).map((date) => (
                                        <option key={format(date, "MM.dd.yyyy")} value={format(date, "yyyy-MM-dd")}>
                                            {format(date, "MMM d, yyyy")}
                                        </option>
                                    ))}
                                </SelectControlStyled>
                                {!item?.subscriptionWeekly &&
                                    <SchedulerMonths frequency={values.reorderPeriod}
                                                     startDate={parseISO(values.nextStartDate)}/>
                                }
                                <FormLabel fontSize={14} fontWeight={"bold"} pt={2}>
                                    Dealership:
                                </FormLabel>
                                <SelectControlStyled selectProps={{fontSize: 14}} name="dealershipId"
                                                     isDisabled={!!editItem || isDisabledSelDealer}>
                                    {[...(dealerships), {id: 0, name: "All"}].map((dealership) => (
                                        <option key={dealership.id} value={String(dealership.id)}>
                                            {dealership.name}
                                        </option>
                                    ))}
                                </SelectControlStyled>
                                {isRoleAdmin() &&
                                    <Flex pt={2}>
                                        <Checkbox
                                            style={{fontSize: "14px"}}
                                            size={"sm"}
                                            isChecked={values.isAdmin}
                                            isDisabled={!!editItem}
                                            children={"Scheduled by admin"}
                                            onChange={(ev: any) => setFieldValue("isAdmin", ev.target.checked, true)}
                                        />
                                    </Flex>
                                }
                                <Flex position="relative" w={"100%"} pt={2}>
                                    <Text fontSize={14} color={localEditItem ? 'blue.500' : 'green.500'}
                                          pt={1}>
                                        {localEditItem ? 'Editing...' : 'Creating subscribe...'}
                                    </Text>
                                    {localEditItem && localEditItem?.id &&
                                        <MainButton ml={"auto"}
                                                    onClick={() => confirmRemove(localEditItem?.id)}>
                                            Unsubscribe
                                        </MainButton>
                                    }
                                    <MainButton ml={localEditItem ? 2 : "auto"}
                                                type="submit"
                                                isLoading={isSubmitting}
                                    > {localEditItem ? 'Update' : 'Create'} </MainButton>
                                </Flex>
                            </Form>
                        </FormControl>
                    )}
                </Formik>
            </Box>
        );
    };

    return (
        <ResponsivePopoverDrawer
            trigger={
                <IconButton
                    bg={"transparent"}
                    _hover={{bg: "transparent"}}
                    pb={"2px"}
                    _active={{bg: "transparent"}}
                    onClick={onOpen}
                    aria-label="Load Vendor Statement"
                    size={"xs"}
                >
                    {isSSTable ? (
                        <IconWithTooltip icon={FiEdit} fontSize={"16"}/>
                    ) : (
                        <MainButton
                            height={"20px"}
                            variantStyle={editItem ? 'subscribed' : 'subscribe2'}
                            maxWidth={"90px"}
                        >
                            {editItem ? 'subscribed' : 'subscribe'}
                        </MainButton>
                    )}
                </IconButton>
            }
            header={
                <Text fontSize={14} isTruncated fontWeight={"bold"}>
                    {item?.name}
                </Text>
            }
        >
            {renderFormContent()}
        </ResponsivePopoverDrawer>
    );
};

export default AddScheduledShipmentPopover;
